import type { AskHistoryTurn, NotebookChapterSpec, TableSpec } from '../../types';
import type { ThreadMessage } from './threadTypes';

/** Turns of the thread sent with each question (server keeps the last 12). */
export const THREAD_HISTORY_LIMIT = 12;

function widgetWindowNote(type: string): string {
  if (type.startsWith('live.')) return 'tonight';
  if (type.startsWith('horizon.')) return 'horizon';
  if (type.startsWith('perf.')) return 'top-bar';
  return '';
}

function tableRange(table: TableSpec): string | null {
  const data = table.data ?? [];
  const numericKey = table.columns.find(column =>
    data.some(row => typeof row[column.key] === 'number'),
  )?.key;
  if (!numericKey) return null;
  const values = data
    .map(row => row[numericKey])
    .filter((value): value is number => typeof value === 'number');
  if (!values.length) return null;
  return `${numericKey} ${Math.min(...values)}–${Math.max(...values)}`;
}

/** What the card showed, so a follow-up can refer to it ("that table"). */
export function cardDigest(card: NotebookChapterSpec): string {
  const window = card.windowLabel ? ` · ${card.windowLabel}` : '';
  const parts = [`Card shown: ${card.title?.trim() || card.template}${window}`];
  for (const table of card.tables ?? []) {
    const count = table.rowCount ?? table.data?.length ?? 0;
    const range = tableRange(table);
    parts.push(`  Table "${table.title}": ${count} rows${range ? `, ${range}` : ''}`);
  }
  for (const chart of card.charts ?? []) {
    parts.push(`  Chart "${chart.title}" (${chart.type})`);
  }
  for (const kpi of card.kpis ?? []) {
    parts.push(`  KPI ${kpi.label}: ${kpi.value}`);
  }
  for (const widget of card.widgets ?? []) {
    const note = widgetWindowNote(widget.type);
    parts.push(`  Widget ${widget.type}${note ? ` (${note})` : ''}`);
  }
  return parts.join('\n');
}

export function buildThreadHistory(
  messages: ThreadMessage[],
  limit = THREAD_HISTORY_LIMIT,
): AskHistoryTurn[] {
  return messages
    // A context change is the conversation's, not a turn: the server states
    // the conversation's context with every question.
    .filter((m): m is Exclude<ThreadMessage, { role: 'context' }> => m.role !== 'context')
    .filter(m => !(m.role === 'assistant' && m.error))
    .slice(-limit)
    .map(m => {
      if (m.role === 'user') {
        return {
          role: 'user' as const,
          content: m.focus ? `(About: ${m.focus.label}) ${m.text}` : m.text,
        };
      }
      const digest = m.card ? `\n\n[${cardDigest(m.card)}]` : '';
      return {
        role: 'assistant' as const,
        content: `${m.text}${digest}`,
        ...(m.receipts?.length ? { receipts: m.receipts } : {}),
      };
    });
}
